/**
 * Lightweight event tracking utility.
 * Fires Google Analytics (gtag) events when available,
 * stores events in the database for the built-in dashboard,
 * and logs to console in dev for debugging.
 */

import { supabase } from "@/integrations/supabase/client";

type TrackingEvent = {
  action: string;
  category: string;
  label?: string;
  value?: number;
};

declare global {
  interface Window {
    __mpSessionId?: string;
  }
}

// Simple session ID (persisted per tab)
function getSessionId(): string {
  if (!window.__mpSessionId) {
    window.__mpSessionId = crypto.randomUUID();
  }
  return window.__mpSessionId;
}

export function trackEvent({ action, category, label, value }: TrackingEvent) {
  // Store in database (fire-and-forget)
  if (typeof window !== "undefined") {
    supabase.from("analytics_events").insert([{
      event_name: action,
      event_category: category,
      event_label: label || null,
      event_value: value || null,
      page_path: window.location.pathname,
      referrer: document.referrer || null,
      session_id: getSessionId(),
    }]).then(() => {});
  }

  // Dev logging
  if (import.meta.env.DEV) {
    console.log(`[Track] ${category}/${action}`, label ?? "");
  }
}

// Track page views
export function trackPageView(path?: string) {
  trackEvent({
    action: "page_view",
    category: "navigation",
    label: path || window.location.pathname,
  });
}

export function trackPhoneClick(location: string) {
  trackEvent({
    action: "click_to_call",
    category: "engagement",
    label: location,
  });
}

export function trackQuoteStart(insuranceType: string) {
  trackEvent({
    action: "quote_start",
    category: "conversion",
    label: insuranceType,
  });
}

export function trackQuoteStep(step: number) {
  trackEvent({
    action: "quote_step",
    category: "conversion",
    label: `step_${step}`,
    value: step,
  });
}

export function trackQuoteSubmit(insuranceType: string) {
  trackEvent({
    action: "quote_submit",
    category: "conversion",
    label: insuranceType,
  });
}

export function trackLeadMagnet(formType: string) {
  trackEvent({
    action: "lead_magnet",
    category: "conversion",
    label: formType,
  });
}
